import { InvoiceStatusLabel } from '@app/components/invoices/InvoiceStatusLabel';
import { Button } from '@app/components/various/Button';
import { Card } from '@app/components/various/Card';
import { Download } from '@app/components/various/Icons';
import { Status } from '@app/components/various/Status';
import { Tooltip } from '@app/components/various/Tooltip';
import { getCalculatedInvoiceStatus } from '@app/logic/invoices';
import { compiledPaths } from '@app/paths';
import { useOrderInvoiceDownload } from '@app/services/hooks/invoice/useOrderInvoiceDownload';
import { Id, Invoice } from '@typings';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface Props {
  invoice: Invoice.Standard;
  orderId: Id;
}

export const CheckoutSuccessInvoiceCard = ({ invoice, orderId }: Props) => {
  const { address, taxes, invoice: invoiceId, invoiceNumber, invoiceHash, isPayable } = invoice;
  const { t } = useTranslation(['invoices', 'common', 'totals']);

  const { downloadOrderInvoice } = useOrderInvoiceDownload();

  const status = getCalculatedInvoiceStatus(invoice);

  const handleDownloadClick = () => {
    downloadOrderInvoice({ invoiceId, invoiceNumber, orderId });
  };

  return (
    <Card.Wrapper variant="gray">
      <Card.Header>
        <Status type={status} /> <InvoiceStatusLabel status={status} />
      </Card.Header>
      <Card.Title>{t('invoices:invoice_number', { invoiceNumber: invoice.invoiceNumber })}</Card.Title>
      <Card.AttributeList>
        <Card.Attribute name={t('common:created')} value={invoice.createdAt} />
        <Card.Attribute name={t('common:due_date')} value={invoice.dueDate} />
        <Card.Attribute name={t('common:company')} value={invoice.company} />
        <Card.Attribute name={t('common:shipment_one')} value={invoice.shipmentId} />
        <Card.Attribute name={t('common:address')} value={address} />
        <Card.Attribute name={t('totals:total_no_tax')} value={invoice.grandTotalWithoutTax} />
        {taxes.map((tax, idx) => (
          <>
            <Card.Attribute
              key={`taxPercent-${idx}`}
              name={t('common:tax_percent', { taxPercent: tax.taxPercent })}
              value={tax.totalPriceTax}
            />
          </>
        ))}
        <Card.Attribute name={t('totals:total_with_tax')} value={invoice.grandTotal} />
        <Card.Attribute name={t('totals:total_due_with_tax')} value={invoice.totalDue} />
      </Card.AttributeList>
      <Tooltip content={t('invoices:download_invoice')}>
        <Button
          size="medium"
          variant="bordered"
          color="secondary"
          aria-label={t('invoices:download_invoice')}
          className="absolute right-4 top-4 z-interactiveTableItem"
          icon={Download}
          onClick={handleDownloadClick}
        />
      </Tooltip>

      {status === 'notPaid' && isPayable && (
        <Button as={Link} to={compiledPaths.INVOICE_DETAILS({ invoiceHash, invoiceId })} size="medium" className="mt-2 w-fit">
          {t('invoices:pay_invoice')}
        </Button>
      )}
    </Card.Wrapper>
  );
};
